/**
 * SIM-1678 — side drawer showing the raw BigQuery warehouse schema (dataset → table → column) as a
 * read-only reference. Reference view only: schema-connection state is shown inline on Tables and
 * Fields rows, and enrichment happens by adding field definitions, not from this tree.
 */
import {
  Button,
  QueryView,
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@simplicity/ui";
import { useQueryClient } from "@tanstack/react-query";
import type { WarehouseColumn, WarehouseSchema, WarehouseTable } from "./types";
import { qk, useWarehouseSchema } from "./useDottieContext";

function ColumnRow({ column }: { column: WarehouseColumn }) {
  return (
    <tr>
      <td className="font-mono text-xs text-foreground">{column.name}</td>
      <td className="font-mono text-xs text-muted-foreground">{column.type}</td>
      <td className="text-xs text-muted-foreground">{column.mode}</td>
    </tr>
  );
}

function TableSection({ table }: { table: WarehouseTable }) {
  return (
    <details
      className="ml-4 rounded-md border border-border"
      data-testid={`drawer-table-${table.tableName}`}
    >
      <summary className="flex cursor-pointer items-center justify-between gap-2 px-3 py-2 text-sm hover:bg-muted/40">
        <span className="font-medium text-foreground">{table.tableName}</span>
        <span className="text-xs text-muted-foreground">{table.columns.length} cols</span>
      </summary>
      <div className="border-t border-border p-2">
        {table.columns.length === 0 ? (
          <p className="px-2 py-1 text-xs text-muted-foreground">No columns.</p>
        ) : (
          <table className="w-full text-left">
            <thead>
              <tr className="text-xs text-muted-foreground">
                <th className="px-2 py-1">Column</th>
                <th className="px-2 py-1">Type</th>
                <th className="px-2 py-1">Mode</th>
              </tr>
            </thead>
            <tbody>
              {table.columns.map((column) => (
                <ColumnRow key={column.name} column={column} />
              ))}
            </tbody>
          </table>
        )}
      </div>
    </details>
  );
}

function SchemaTree({ data }: { data: WarehouseSchema }) {
  if (data.datasets.length === 0) {
    return (
      <p className="text-sm text-muted-foreground">
        Warehouse is empty — no silver or gold datasets reachable for this tenant.
      </p>
    );
  }
  return (
    <div className="space-y-3" data-testid="warehouse-schema-tree">
      {data.datasets.map((dataset) => (
        <details
          key={dataset.datasetId}
          className="rounded-md border border-border"
          data-testid={`drawer-dataset-${dataset.datasetId}`}
        >
          <summary className="flex cursor-pointer items-center justify-between gap-2 px-3 py-2 hover:bg-muted/40">
            <span className="font-semibold text-foreground">{dataset.datasetId}</span>
            <span className="text-xs text-muted-foreground">{dataset.tables.length} tables</span>
          </summary>
          <div className="space-y-2 border-t border-border p-2">
            {dataset.tables.map((table) => (
              <TableSection key={table.tableName} table={table} />
            ))}
          </div>
        </details>
      ))}
    </div>
  );
}

export function WarehouseSchemaDrawer({
  tenantId,
  open,
  onOpenChange,
}: {
  tenantId: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const warehouseQuery = useWarehouseSchema(tenantId);
  const qc = useQueryClient();

  const handleRefresh = () => {
    void qc.invalidateQueries({ queryKey: qk.warehouseSchema(tenantId) });
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="flex w-full flex-col gap-4 sm:max-w-lg">
        <SheetHeader>
          <SheetTitle>Warehouse schema</SheetTitle>
          <SheetDescription>
            Read-only BigQuery schema for this tenant. Use Tables and Fields above to author
            descriptions; this drawer is a reference view.
          </SheetDescription>
        </SheetHeader>
        <div className="flex items-center justify-between">
          <p className="text-xs text-muted-foreground">
            {warehouseQuery.data?.fetchedAt &&
              `Fetched ${new Date(warehouseQuery.data.fetchedAt).toLocaleString()}`}
          </p>
          <Button
            variant="outline"
            size="sm"
            onClick={handleRefresh}
            disabled={warehouseQuery.isFetching}
          >
            Refresh
          </Button>
        </div>
        <div className="overflow-y-auto" data-testid="warehouse-drawer-body">
          <QueryView
            query={warehouseQuery}
            error={(err) => (
              <p className="text-sm text-destructive" role="alert">
                Failed to load warehouse schema: {err.message}
              </p>
            )}
          >
            {(data) => (
              <div className="space-y-3">
                {data.error && (
                  <p
                    role="alert"
                    className="rounded-md bg-destructive/10 p-3 text-sm text-destructive"
                    data-testid="warehouse-drawer-error-banner"
                  >
                    {data.error}
                  </p>
                )}
                <SchemaTree data={data} />
              </div>
            )}
          </QueryView>
        </div>
      </SheetContent>
    </Sheet>
  );
}
